import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, ROLES, ROLE_LABELS } from '../contexts/AuthContext';
import { usersService, sitesService, getErrorMessage } from '../services/api';
import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  Plus, 
  Pencil, 
  Trash2,
  Search,
  AlertCircle,
  RefreshCw,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

export default function UsersPage() {
  const { user: currentAuthUser, isSuperAdmin, isAdmin, isSuperOrAdmin, isHesapYoneticisi, getEffectiveRole, getEffectiveSiteId } = useAuth();
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Simülasyon desteği
  const effectiveRole = getEffectiveRole ? getEffectiveRole() : currentAuthUser?.role;
  const effectiveSiteId = getEffectiveSiteId ? getEffectiveSiteId() : currentAuthUser?.site_id;
  const isEffectiveHesapYoneticisi = effectiveRole === 'hesap_yoneticisi' || effectiveRole === 'site_manager';
  
  // Rol kontrolleri
  const isSiteManager = currentAuthUser?.role === ROLES.SITE_MANAGER;
  const isAdminRole = currentAuthUser?.role === ROLES.ADMIN;
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingUserId, setEditingUserId] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state - varsayılan rol site_sakini
  const [form, setForm] = useState({
    email: '',
    password: '',
    full_name: '',
    role: ROLES.SITE_SAKINI,
    is_active: true,
    site_id: '',
    apartment_number: '',
    account_name: ''
  });
  
  // Site dropdown search state
  const [siteSearchTerm, setSiteSearchTerm] = useState('');
  const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

  const editingUser = useMemo(() => {
    if (!editingUserId) return null;
    return users.find(u => u.id === editingUserId);
  }, [editingUserId, users]);

  const isEditingMainAdmin = useMemo(() => {
    return editingUser?.email === 'admin@hims.com';
  }, [editingUser]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      let [usersData, sitesData] = await Promise.all([
        usersService.list(),
        sitesService.list()
      ]);
      
      // API response kontrolü - array veya obje olabilir
      const usersArray = Array.isArray(usersData) ? usersData : (usersData?.users || usersData?.items || usersData?.data || []);
      const sitesArray = Array.isArray(sitesData) ? sitesData : (sitesData?.sites || sitesData?.items || sitesData?.data || []);
      
      // Teknisyenleri (calisan rolü ve technician_type olanları) gizle
      let filteredUsers = usersArray.filter(u => 
        u.role !== 'calisan' && !u.technician_type && !u.is_technician
      );
      
      // Hesap Yöneticisi için sadece kendi sitesinin kullanıcılarını göster
      if (isEffectiveHesapYoneticisi && effectiveSiteId) {
        filteredUsers = filteredUsers.filter(u => u.site_id === effectiveSiteId);
        sitesData = sitesArray.filter(s => s.id === effectiveSiteId);
      } else {
        sitesData = sitesArray;
      }
      
      setUsers(filteredUsers);
      setSites(sitesData);
      setError(null);
    } catch (err) {
      console.error('Kullanıcı verileri yüklenemedi:', err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [isEffectiveHesapYoneticisi, effectiveSiteId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    setForm({
      email: '',
      password: '',
      full_name: '',
      role: ROLES.SITE_SAKINI,
      is_active: true,
      site_id: '',
      apartment_number: '',
      account_name: ''
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingUserId(null);
    setModalMode('create');
    setSaving(false);
    setModalOpen(true);
  };

  const openEditModal = (user) => {
    setForm({
      email: user.email,
      password: '',
      full_name: user.full_name || '',
      role: user.role,
      is_active: user.is_active,
      site_id: user.site_id || '',
      apartment_number: user.apartment_number || '',
      account_name: user.account_name || ''
    });
    setEditingUserId(user.id);
    setModalMode('edit');
    setSaving(false);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setSiteDropdownOpen(false);
    setSiteSearchTerm('');
    setTimeout(() => {
      setEditingUserId(null);
      resetForm();
    }, 200);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving) return;
    
    setSaving(true);
    
    try {
      if (modalMode === 'create') {
        await usersService.create({
          email: form.email,
          password: form.password,
          full_name: form.full_name,
          role: form.role,
          is_active: form.is_active,
          site_id: form.site_id || null,
          apartment_number: form.apartment_number || null,
          account_name: form.account_name || null
        });
        toast.success('Kullanıcı başarıyla oluşturuldu');
      } else {
        const updateData = {
          full_name: form.full_name,
          is_active: form.is_active,
          site_id: form.site_id || null,
          apartment_number: form.apartment_number || null,
          account_name: form.account_name || null
        };
        
        if (!isEditingMainAdmin) {
          updateData.role = form.role;
        }
        
        if (!isEditingMainAdmin && editingUser && form.email !== editingUser.email) {
          updateData.email = form.email;
        }
        
        if (form.password) {
          updateData.password = form.password;
        }
        
        await usersService.update(editingUserId, updateData);
        toast.success('Kullanıcı başarıyla güncellendi');
      }
      
      setSaving(false);
      setModalOpen(false);
      fetchData();
      
    } catch (err) {
      toast.error(getErrorMessage(err));
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (user.email === 'admin@hims.com') {
      toast.error('Ana admin kullanıcısı silinemez');
      return;
    }
    
    if (!window.confirm(`"${user.full_name || user.email}" kullanıcısını silmek istediğinize emin misiniz?`)) {
      return;
    }
    
    try {
      await usersService.delete(user.id);
      toast.success('Kullanıcı başarıyla silindi');
      fetchData();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const updateForm = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const filteredUsers = users.filter(user => 
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.full_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const variants = {
      [ROLES.SUPER_ADMIN]: 'info',
      [ROLES.ADMIN]: 'warning',
      [ROLES.SITE_MANAGER]: 'default',
      [ROLES.HESAP_YONETICISI]: 'default',
      [ROLES.BIREYSEL_YONETICI]: 'success',
      [ROLES.BIREYSEL_HESAP]: 'success',
      [ROLES.SITE_SAKINI]: 'secondary',
      [ROLES.SAKINI]: 'secondary',
      [ROLES.SOFOR]: 'secondary',
      [ROLES.CALISAN]: 'secondary',
      [ROLES.ZIYARETCI]: 'secondary',
      [ROLES.USER]: 'secondary'
    };
    return <NativeBadge variant={variants[role] || 'secondary'}>{ROLE_LABELS[role] || role}</NativeBadge>;
  };

  // Rol seçeneklerini belirle - mevcut kullanıcı rolüne göre
  const getRoleOptions = () => {
    // Süper Admin hariç tüm roller
    const allRoles = [
      { value: ROLES.ADMIN, label: 'Admin' },
      { value: ROLES.HESAP_YONETICISI, label: 'Hesap Yöneticisi' },
      { value: ROLES.BIREYSEL_HESAP, label: 'Bireysel Hesap' },
      { value: ROLES.SITE_SAKINI, label: 'Site Sakini' },
      { value: ROLES.SOFOR, label: 'Şoför' },
      { value: ROLES.CALISAN, label: 'Çalışan' },
      { value: ROLES.ZIYARETCI, label: 'Ziyaretçi' }
    ];
    
    // Super Admin tüm rolleri görebilir (admin dahil, süper admin hariç)
    if (isSuperAdmin()) {
      return allRoles;
    }
    
    // Admin: hesap_yoneticisi, site_sakini, sofor, calisan, ziyaretci
    if (isAdminRole) {
      return [
        { value: ROLES.HESAP_YONETICISI, label: 'Hesap Yöneticisi' },
        { value: ROLES.SITE_SAKINI, label: 'Site Sakini' },
        { value: ROLES.SOFOR, label: 'Şoför' },
        { value: ROLES.CALISAN, label: 'Çalışan' },
        { value: ROLES.ZIYARETCI, label: 'Ziyaretçi' }
      ];
    }
    
    // Hesap Yöneticisi: site_sakini, sofor, calisan, ziyaretci
    return [
      { value: ROLES.SITE_SAKINI, label: 'Site Sakini' },
      { value: ROLES.SOFOR, label: 'Şoför' },
      { value: ROLES.CALISAN, label: 'Çalışan' },
      { value: ROLES.ZIYARETCI, label: 'Ziyaretçi' }
    ];
  };

  const getSiteName = (siteId) => {
    const site = sites.find(s => s.id === siteId);
    return site?.name || '-';
  };

  // Site dropdown için filtrelenmiş siteler
  const filteredSites = sites.filter(site =>
    site.name?.toLowerCase().includes(siteSearchTerm.toLowerCase())
  );

  // Dropdown dışına tıklanınca kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (siteDropdownOpen && !event.target.closest('.site-dropdown-container')) {
        setSiteDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [siteDropdownOpen]);

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="users-page">
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>Kullanıcılar</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Sistem kullanıcılarını yönetin</p>
        </div>
        <NativeButton onClick={openCreateModal} data-testid="create-user-button">
          <Plus style={{ width: '16px', height: '16px' }} />
          Yeni Kullanıcı
        </NativeButton>
      </div>

      {error && (
        <NativeAlert variant="danger" icon={AlertCircle}>
          {error}
        </NativeAlert>
      )}

      {/* Search */}
      <NativeCard>
        <NativeCardContent>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: '200px' }}>
              <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', width: '16px', height: '16px' }} className="text-slate-400" />
              <input
                type="text"
                placeholder="Kullanıcı ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px 8px 40px',
                  borderRadius: '8px',
                  border: '1px solid var(--card-border)',
                  fontSize: '14px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  color: 'var(--text-primary)',
                  backgroundColor: 'var(--card-bg)'
                }}
                className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
              />
            </div>
            <NativeButton variant="outline" onClick={fetchData}>
              <RefreshCw style={{ width: '16px', height: '16px' }} />
              Yenile
            </NativeButton>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Users Table */}
      <NativeCard>
        <NativeCardHeader>
          <NativeCardTitle icon={Users}>
            Kullanıcı Listesi ({filteredUsers.length})
          </NativeCardTitle>
        </NativeCardHeader>
        <NativeCardContent style={{ padding: 0 }}>
          <NativeTable>
            <NativeTableHeader>
              <NativeTableHead>Ad Soyad</NativeTableHead>
              <NativeTableHead>E-posta</NativeTableHead>
              <NativeTableHead>Rol</NativeTableHead>
              <NativeTableHead>Site</NativeTableHead>
              <NativeTableHead>Daire No</NativeTableHead>
              <NativeTableHead>Durum</NativeTableHead>
              <NativeTableHead align="right">Islemler</NativeTableHead>
            </NativeTableHeader>
            <NativeTableBody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <EmptyState
                      icon={Users}
                      title="Kullanıcı bulunamadı"
                      description="Henüz kullanıcı eklenmemiş veya arama kriterlerinize uygun kullanıcı yok"
                    />
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <NativeTableRow key={user.id}>
                    <NativeTableCell>
                      <span style={{ fontWeight: '500' }}>{user.full_name || '-'}</span>
                    </NativeTableCell>
                    <NativeTableCell>{user.email}</NativeTableCell>
                    <NativeTableCell>{getRoleBadge(user.role)}</NativeTableCell>
                    <NativeTableCell>{getSiteName(user.site_id)}</NativeTableCell>
                    <NativeTableCell>{user.apartment_number || '-'}</NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant={user.is_active ? 'success' : 'secondary'}>
                        {user.is_active ? 'Aktif' : 'Pasif'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                        <NativeButton
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditModal(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil style={{ width: '16px', height: '16px' }} />
                        </NativeButton>
                        {/* Site Manager kullanıcı silemez */}
                        {isSuperAdmin() && (
                          <NativeButton
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user)}
                            disabled={user.email === 'admin@hims.com'}
                            style={{ color: user.email === 'admin@hims.com' ? '#9ca3af' : '#dc2626' }}
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 style={{ width: '16px', height: '16px' }} />
                          </NativeButton>
                        )}
                      </div>
                    </NativeTableCell>
                  </NativeTableRow>
                ))
              )}
            </NativeTableBody>
          </NativeTable>
        </NativeCardContent>
      </NativeCard>

      {/* Create/Edit Modal */}
      <NativeModal
        isOpen={modalOpen}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Yeni Kullanıcı Oluştur' : 'Kullanıcı Düzenle'}
        disabled={saving}
        maxWidth="500px"
      >
        <form onSubmit={handleSubmit}>
          <NativeInput
            label="Ad Soyad"
            id="full_name"
            value={form.full_name}
            onChange={(e) => updateForm('full_name', e.target.value)}
            placeholder="Ahmet Yilmaz"
            required
          />

          <NativeInput
            label="E-posta"
            id="email"
            type="email"
            value={form.email}
            onChange={(e) => updateForm('email', e.target.value)}
            placeholder="ornek@email.com"
            required
            disabled={isEditingMainAdmin}
          />
          {isEditingMainAdmin && (
            <p style={{ fontSize: '12px', color: '#d97706', marginTop: '-8px', marginBottom: '12px' }}>Ana admin e-postasi degistirilemez</p>
          )}

          <NativeInput
            label={modalMode === 'create' ? 'Sifre' : 'Sifre (bos birakilirsa degismez)'}
            id="password"
            type="password"
            value={form.password}
            onChange={(e) => updateForm('password', e.target.value)}
            placeholder="********"
            required={modalMode === 'create'}
          />

          {/* Site Manager sadece site_sakini rolünde kullanıcı ekleyebilir - rol seçimi gizli */}
          {!isSiteManager && (
            <NativeSelect
              label="Rol"
              id="role"
              value={form.role}
              onChange={(e) => updateForm('role', e.target.value)}
              disabled={isEditingMainAdmin}
              options={getRoleOptions()}
            />
          )}
          {isSiteManager && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }} className="text-slate-700 dark:text-slate-200">Rol</label>
              <div style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }} className="dark:bg-slate-700 dark:border-slate-600">
                <span className="text-slate-600 dark:text-slate-300">{ROLE_LABELS[ROLES.SITE_SAKINI]}</span>
              </div>
              <p style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>Site yöneticisi olarak sadece Site Sakini ekleyebilirsiniz</p>
            </div>
          )}
          {isEditingMainAdmin && (
            <p style={{ fontSize: '12px', color: '#d97706', marginTop: '-8px', marginBottom: '12px' }}>Ana admin rolu degistirilemez</p>
          )}

          {/* Site Manager için site seçimi gizli - otomatik kendi sitesi atanır */}
          {!isSiteManager && (
            <div style={{ marginBottom: '16px' }} className="site-dropdown-container">
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }} className="text-slate-700 dark:text-slate-200">
                Site
              </label>
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '8px',
                    border: '1px solid var(--card-border)',
                    backgroundColor: 'var(--card-bg)',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                  className="dark:bg-slate-700 dark:border-slate-600"
                  data-testid="site-dropdown-trigger"
                >
                  <span className={form.site_id ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}>
                    {form.site_id ? getSiteName(form.site_id) : 'Secim Yok'}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9l6 6 6-6"/>
                  </svg>
                </div>
                
                {siteDropdownOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      marginTop: '4px',
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '8px',
                      boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                      zIndex: 50,
                      maxHeight: '280px',
                      overflow: 'hidden',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    className="dark:bg-slate-800 dark:border-slate-600"
                  >
                    {/* Arama Input */}
                    <div style={{ padding: '8px', borderBottom: '1px solid var(--card-border)' }} className="dark:border-slate-600">
                      <div style={{ position: 'relative' }}>
                        <Search style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px' }} className="text-slate-400" />
                        <input
                          type="text"
                          placeholder="Site ara..."
                          value={siteSearchTerm}
                          onChange={(e) => setSiteSearchTerm(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            width: '100%',
                            padding: '8px 10px 8px 32px',
                            borderRadius: '6px',
                            border: '1px solid var(--card-border)',
                            fontSize: '13px',
                            outline: 'none',
                            boxSizing: 'border-box',
                            color: 'var(--text-primary)',
                            backgroundColor: 'var(--card-bg)'
                          }}
                          className="dark:bg-slate-700 dark:border-slate-600 dark:text-white"
                          autoFocus
                          data-testid="site-search-input"
                        />
                      </div>
                    </div>
                    
                    {/* Site Listesi */}
                    <div style={{ overflowY: 'auto', maxHeight: '220px' }}>
                      {/* Seçim Yok seçeneği */}
                      <div
                        onClick={() => {
                          updateForm('site_id', '');
                          setSiteDropdownOpen(false);
                          setSiteSearchTerm('');
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          backgroundColor: !form.site_id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                          borderLeft: !form.site_id ? '3px solid #3b82f6' : '3px solid transparent'
                        }}
                        className="hover:bg-slate-100 dark:hover:bg-slate-700"
                        data-testid="site-option-none"
                      >
                        <span className="text-slate-500 dark:text-slate-400" style={{ fontSize: '14px' }}>Secim Yok</span>
                      </div>
                      
                      {filteredSites.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center' }}>
                          <span className="text-slate-400 dark:text-slate-500" style={{ fontSize: '13px' }}>Site bulunamadı</span>
                        </div>
                      ) : (
                        filteredSites.map((site) => (
                          <div
                            key={site.id}
                            onClick={() => {
                              updateForm('site_id', site.id);
                              setSiteDropdownOpen(false);
                              setSiteSearchTerm('');
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              backgroundColor: form.site_id === site.id ? 'rgba(59, 130, 246, 0.1)' : 'transparent',
                              borderLeft: form.site_id === site.id ? '3px solid #3b82f6' : '3px solid transparent'
                            }}
                            className="hover:bg-slate-100 dark:hover:bg-slate-700"
                            data-testid={`site-option-${site.id}`}
                          >
                            <span className="text-slate-900 dark:text-white" style={{ fontSize: '14px' }}>{site.name}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {isSiteManager && (
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px' }} className="text-slate-700 dark:text-slate-200">Site</label>
              <div style={{ padding: '10px 12px', borderRadius: '8px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)' }} className="dark:bg-slate-700 dark:border-slate-600">
                <span className="text-slate-600 dark:text-slate-300">
                  {sites.find(s => s.id === currentAuthUser?.site_id)?.name || 'Kendi siteniz'}
                </span>
              </div>
              <p style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px' }}>Kullanıcı otomatik olarak sitenize eklenecektir</p>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            <NativeInput
              label="Daire No"
              id="apartment_number"
              value={form.apartment_number}
              onChange={(e) => updateForm('apartment_number', e.target.value)}
              placeholder="A-15"
            />
            <NativeInput
              label="Cari Adi"
              id="account_name"
              value={form.account_name}
              onChange={(e) => updateForm('account_name', e.target.value)}
              placeholder="Sirket adi"
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => updateForm('is_active', e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
            />
            <label htmlFor="is_active" className="text-slate-700 dark:text-slate-200" style={{ fontSize: '14px', cursor: 'pointer' }}>
              Aktif
            </label>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
            <NativeButton type="button" variant="outline" onClick={closeModal} disabled={saving}>
              Iptal
            </NativeButton>
            <NativeButton type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} />
                  Kaydediliyor...
                </>
              ) : (
                modalMode === 'create' ? 'Oluştur' : 'Kaydet'
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>
    </div>
  );
}
